import moment from "moment";
import React from "react";
import { Message, TextArea } from "semantic-ui-react";
import { Input, TimePicker } from "../../../components";
import { FilterDatePicker } from "components/DatePicker/SingleDatePicker";
import { isDepartureTooEarly } from "../../flightvalidator";
import { PrivateFlightEditProps } from "../../model";
import AirportSelection from "./airportselection";

interface Props {
  segment: PrivateFlightEditProps;
  onSegmentChange: (editedSegment: PrivateFlightEditProps) => void;

  noDepartureAirportError: boolean;
  noArrivalAirportError: boolean;
  departureTooEarly: boolean;
}

export default class PrivateJetEdit extends React.Component<Props> {

  onTimeChange = (selectedTimeMoment: moment.Moment, current: moment.Moment, onChange: (arrival: moment.Moment) => void) => {
    const newMoment = current.clone().startOf("day")
      .add(selectedTimeMoment.hours(), "hours")
      .add(selectedTimeMoment.minutes(), "minutes");

    onChange(newMoment);
  };

  onDepartureTimeChange = (selectedTimeMoment: moment.Moment) => {
    this.onTimeChange(selectedTimeMoment, this.props.segment.departure, (newMoment) => this.onSegmentChange({ departure: newMoment }));
  };

  onArrivalTimeChange = (selectedTimeMoment: moment.Moment) => {
    this.onTimeChange(selectedTimeMoment, this.props.segment.arrival, (newMoment) => this.onSegmentChange({ arrival: newMoment }));
  };

  onSegmentChange = (newSegment: Partial<PrivateFlightEditProps>) => {
    const newProps = { ...this.props.segment, ...newSegment };
    this.props.onSegmentChange(newProps);
  };

  public render(): JSX.Element {

    const { arrival, departure, arrivalAirport, departureAirport, departureTerminal,
      arrivalTerminal, departureGate, arrivalGate, access, confirmationNumber, aircraft, capacity } = this.props.segment;

    const departureTooEarly = isDepartureTooEarly(departure, arrival);
    return <>
      <table className="s1_table bordergrid gray">
        <tbody>
          <tr>
            <th /><th>{RESX.Flight.Departure}</th>
            <th>{RESX.Flight.Arrival}</th>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Departure} / {RESX.Flight.Arrival}</td>
            <td style={{ width: 385 }}>
              <div className="flex">
                <div className="margin-right-small">
                  <FilterDatePicker
                    dontUpdateOnInputChange
                    minDate={moment(serverReference.constants.appointment.minDate)}
                    maxDate={moment(serverReference.constants.appointment.maxDate)}
                    onChange={d => this.onSegmentChange({ departure: d })}
                    value={departure}
                  />
                </div>
                <div>
                  <TimePicker
                    value={departure}
                    onChange={this.onDepartureTimeChange}
                  />
                </div>
              </div>

              {departureTooEarly &&
                <Message color="red" className="margin-top-small">{RESX.Flight.ArrivalCannotBeBeforeDeparture}</Message>
              }
            </td>
            <td>
              <div className="flex">
                <div className="margin-right-small">
                  <FilterDatePicker
                    dontUpdateOnInputChange
                    minDate={moment(serverReference.constants.appointment.minDate)}
                    maxDate={moment(serverReference.constants.appointment.maxDate)}
                    onChange={d => this.onSegmentChange({ arrival: d })}
                    value={arrival}
                  />
                </div>
                <div>
                  <TimePicker
                    value={arrival}
                    onChange={this.onArrivalTimeChange}
                  />
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Airport}</td>
            <td><AirportSelection error={this.props.noDepartureAirportError} airport={departureAirport} onAirportSelected={a => this.onSegmentChange({ departureAirport: a })} /></td>
            <td><AirportSelection error={this.props.noArrivalAirportError} airport={arrivalAirport} onAirportSelected={a => this.onSegmentChange({ arrivalAirport: a })} /></td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Terminal}</td>
            <td>
              <Input
                value={departureTerminal}
                onChange={(_, { value }) => this.onSegmentChange({ departureTerminal: value })}
                maxLength={serverReference.constants.flight.maxTerminalLength}
                size="small"
                style={{ width: 60 }}
              />
            </td>
            <td>
              <Input
                value={arrivalTerminal}
                onChange={(_, { value }) => this.onSegmentChange({ arrivalTerminal: value })}
                maxLength={serverReference.constants.flight.maxTerminalLength}
                size="small"
                style={{ width: 60 }}
              />
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Gate}</td>
            <td >
              <Input
                value={departureGate}
                onChange={(_, { value }) => this.onSegmentChange({ departureGate: value })}
                maxLength={serverReference.constants.flight.maxGateLength}
                size="small"
                style={{ width: 60 }}
              />
            </td>
            <td>
              <Input
                value={arrivalGate}
                onChange={(_, { value }) => this.onSegmentChange({ arrivalGate: value })}
                maxLength={serverReference.constants.flight.maxGateLength}
                size="small"
                style={{ width: 60 }}
              />
            </td>
          </tr>
          <tr>
            <td className="label">Access</td>
            <td colSpan={2}>
              <TextArea
                maxLength={serverReference.constants.flight.maxPrivateJetAccessLength}
                rows={4}
                value={access}
                onChange={(_, { value }) => this.onSegmentChange({ access: value.toString() })}
              />
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.Aircraft}</td>
            <td colSpan={2}>
              <Input
                fluid
                type="text"
                maxLength={serverReference.constants.flight.maxAircraftLength}
                value={aircraft}
                onChange={(_, { value }) => this.onSegmentChange({ aircraft: value })} />
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Flight.ConfirmationNumber}</td>
            <td colSpan={2}>
              <Input
                width="five"
                maxLength={serverReference.constants.flight.maxPrivateJetConfirmationNumberLength}
                type="text"
                value={confirmationNumber}
                onChange={(_, { value }) => this.onSegmentChange({ confirmationNumber: value })} />
            </td>
          </tr>
          <tr>
            <td className="label">{RESX.Venue.Capacity}</td>
            <td colSpan={2}>
              <Input
                type="text"
                maxLength={4}
                value={capacity}
                restrict={{
                  allowDigits: true,
                  allowLetters: false,
                  allowUnderscore: false
                }}
                onChange={(_, { value }) => this.onSegmentChange({ capacity: value })}
                placeholder={"PAX"}
                style={{ width: 60 }}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </>;
  }
}